import { IStyleConfig, mergeStyles } from '@cian/components';
import { isEqual } from 'lodash';
import * as React from 'react';

const styles = require('./slider.css');

type Slide = (visible: boolean, key: number) => void;

export interface ISliderProps {
  slides: Slide[];
  interval?: number;
  actionDistance?: number;
  containerStyles?: IStyleConfig;
  photoStyles?: IStyleConfig;
  countsStyles?: IStyleConfig;
}

interface ISliderState {
  erroredSlidesCount: number;
  currentIndex: number;
  transition: number;
  action: number;
  x0: number;
  firstImageLoaded: boolean;
  restImagesLoaded: boolean;
  brokenSlides: Slide[];
}

const initialState: ISliderState = {
  action: 0,
  brokenSlides: [] as Slide[],
  currentIndex: 0,
  erroredSlidesCount: 0,
  firstImageLoaded: false,
  restImagesLoaded: false,
  transition: 1,
  x0: 0,
};

export class Slider extends React.PureComponent<ISliderProps, ISliderState> {
  public state = initialState;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private intervalId: any;

  public setSlide(slideNumber: number) {
    this.setState({ currentIndex: slideNumber });
  }

  public transitionEnd = () => {
    const { currentIndex, action } = this.state;

    this.setState({
      action: 0,
      currentIndex: this.validIndex(currentIndex + action),
      transition: 0,
      x0: 0,
    });
  };

  public componentDidMount() {
    this.updateSliding(this.props.slides, this.props.interval, true);
  }

  public UNSAFE_componentWillUpdate(nextProps: ISliderProps) {
    this.updateSliding(nextProps.slides, nextProps.interval);
  }

  public componentWillUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  public render() {
    const { containerStyles, slides } = this.props;
    const sliderStyles = mergeStyles(styles.container, containerStyles);

    return (
      <div {...sliderStyles}>
        {this.renderSlides()}
        {slides.length > 1 && this.renderControls()}
      </div>
    );
  }

  private updateSliding = (slides: Slide[], interval?: number, init?: boolean) => {
    if ((!isEqual(this.props.slides, slides) || init) && interval && slides.length > 1) {
      clearInterval(this.intervalId);
      this.setSlide(0);

      const intervalId = setInterval(this.changeCurrentIndex(1), interval);
      this.intervalId = intervalId;
    }

    if (!interval || slides.length <= 1) {
      clearInterval(this.intervalId);
    }
  };

  private renderControls = () => (
    <div className={styles['arrows-container']}>
      <button className={styles['arrow-left']} onClick={this.changeCurrentIndex(-1)} />

      <button className={styles['arrow-right']} onClick={this.changeCurrentIndex(1)} />
    </div>
  );

  private changeCurrentIndex = (increment: number) => (e?: React.MouseEvent<object>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.step(increment);
  };

  private renderSlides = () => {
    const { slides } = this.props;
    const { action, x0, transition } = this.state;
    const transitionX = action ? -action * 33.333 + '%' : x0 + 'px';
    const allSlidesCount = slides.length;
    const leftPseudoSlide = slides.slice(-1)[0];

    const slidesStyles = mergeStyles(styles.items, transition && styles.transition, {
      transform: `translateX(${transitionX})`,
      WebkitTransform: `translateX(${transitionX})`,
    });

    return (
      <div {...slidesStyles} onTransitionEnd={this.transitionEnd}>
        {this.getSlide(leftPseudoSlide, 0)}
        {slides.map((s, id) => this.getSlide(s, id + 1))}
        {this.getSlide(slides[0], allSlidesCount + 1)}
      </div>
    );
  };

  private validIndex(index: number) {
    const { slides } = this.props;
    const firstIndex = 0;
    const lastIndex = slides.length - 1;

    if (index < firstIndex) {
      return lastIndex;
    }

    if (index > lastIndex) {
      return firstIndex;
    }

    return index;
  }

  private step(action: number) {
    if (this.state.action) {
      this.transitionEnd();
    }

    const nextState = {
      action: action ? action : this.state.action,
      transition: 1,
      x0: action ? this.state.x0 : 0,
    };

    this.setState(nextState);
  }

  private getSlide = (slide: Slide, id: number): React.ReactNode | void => {
    const { currentIndex } = this.state;
    const relativeIndex = id - currentIndex;
    const visible = relativeIndex >= 0 && relativeIndex <= 2;

    return slide(visible, id);
  };
}
